// ./src/utils/api/VenueService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Display } from "./DisplayService";

// GraphQL queries
const VENUE_LIST_QUERY = gql`
  query VenueList($filter: VenueFilter, $params: PaginationAndSorting) {
    venueList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        description
        address1
        address2
        city
        stateProvince
        postalCode
        country
        contactPerson
        email
        phone
        website
        logoUrl
        uploadedFileId
        timezone
        status
        createdAt
        createdBy
        displays {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VENUE_GET_QUERY = gql`
  query VenueGet($id: ID) {
    venueGet(id: $id) {
      code
      data {
        id
        name
        description
        address1
        address2
        city
        stateProvince
        postalCode
        country
        contactPerson
        email
        phone
        website
        logoUrl
        uploadedFileId
        timezone
        status
        createdAt
        createdBy
        displays {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VENUE_GET_MANY_QUERY = gql`
  query VenueGetMany($ids: [ID]) {
    venueGetMany(ids: $ids) {
      code
      data {
        id
        name
        description
        address1
        address2
        city
        stateProvince
        postalCode
        country
        contactPerson
        email
        phone
        website
        logoUrl
        uploadedFileId
        timezone
        status
        createdAt
        createdBy
        displays {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const VENUE_CREATE_MUTATION = gql`
  mutation VenueCreate($params: VenueInput) {
    venueCreate(params: $params) {
      code
      data {
        id
        name
        description
        address1
        address2
        city
        stateProvince
        postalCode
        country
        contactPerson
        email
        phone
        website
        logoUrl
        uploadedFileId
        timezone
        status
        createdAt
        createdBy
        displays {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VENUE_UPDATE_MUTATION = gql`
  mutation VenueUpdate($where: VenueWhereInput, $params: VenueInput) {
    venueUpdate(where: $where, params: $params) {
      code
      data {
        id
        name
        description
        address1
        address2
        city
        stateProvince
        postalCode
        country
        contactPerson
        email
        phone
        website
        logoUrl
        uploadedFileId
        timezone
        status
        createdAt
        createdBy
        displays {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const VENUE_REMOVE_MUTATION = gql`
  mutation VenueRemove($where: VenueWhereInput) {
    venueRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Venue {
  id?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  address1?: string | undefined;
  address2?: string | undefined;
  city?: string | undefined;
  stateProvince?: string | undefined;
  postalCode?: string | undefined;
  country?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  website?: string | undefined;
  contactPerson?: string | undefined;
  logoUrl?: string | undefined;
  uploadedFileId?: string | undefined;
  timezone?: string | undefined;
  status?: number;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  displays: Display[];
}

export interface VenueInput {
  name?: string | null | undefined;
  description?: string | null | undefined;
  address1?: string | null | undefined;
  address2?: string | null | undefined;
  city?: string | null | undefined;
  stateProvince?: string | null | undefined;
  postalCode?: string | null | undefined;
  country?: string | null | undefined;
  phone?: string | null | undefined;
  email?: string | null | undefined;
  website?: string | null | undefined;
  contactPerson?: string | null | undefined;
  logoUrl?: string | null | undefined;
  uploadedFileId?: string | null | undefined;
  timezone?: string | null | undefined;
  status?: number | null | undefined;
}

export interface VenueFilter {
  name?: string;
  status?: string;
  searchKeyword?: string | null | undefined;
  uploadedFileId?: string | null | undefined;
}

export interface VenueWhereInput {
  id?: string | undefined;
}

/**
 * Venue service that handles all venue-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class VenueService {
  /**
   * Get a list of venues with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: VenueFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(VENUE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single venue by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(VENUE_GET_QUERY, {
      venueGetId: id,
    });
    return result;
  }

  /**
   * Get multiple venues by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(VENUE_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new venue
   */
  static async create(params: VenueInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(VENUE_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing venue
   */
  static async update(
    where: VenueWhereInput,
    params: VenueInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(VENUE_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a venue
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(VENUE_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }
}
